import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PanelLeftClose, PanelLeft, BarChart3, Headset, Settings, X, ChevronRight, ExternalLink } from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import { supportApi } from '../../services/supportApi';
import { staffNavItems } from '../../utils/staffNav';
import { useAuth } from '../../hooks/useAuth';
import { useSidebar } from '../../contexts/SidebarContext';
import { getPersonaConfig } from '../../utils/personaConfig';
import { cn } from '../../lib/utils';
import Tooltip from '../ui/Tooltip';
import type { NavItem } from '../../utils/personaConfig';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import PracticeAreaSwitcher from './PracticeAreaSwitcher';
import { usePreferences } from '../../contexts/PreferencesContext';
import { effectiveLens, workspaceLabel, PracticeArea } from '../../types/practiceArea';
import { navItemVisible, readActiveRole } from './navGating';
import { useNavFlags } from './navFlags';
import { launchCorporateSuite } from '../../services/corporateApi';
import LogoLockup from '../chambers/LogoLockup';
import PlanBadge from '../billing/PlanBadge';

/**
 * Chambers-ledger rail.
 * Design reference: chamber-rail surface, brass filled active rows (sharp
 * corners), IBM Plex Mono overline section labels, brass hairline borders.
 * Styling only — nav config, lens filtering, collapse and mobile behaviour
 * are unchanged.
 */

interface NavItemLinkProps {
  item: NavItem;
  collapsed: boolean;
}

const NavItemLink: React.FC<NavItemLinkProps> = ({ item, collapsed }) => {
  const Icon = item.icon;

  const link = (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) =>
        cn(
          'relative flex items-center transition-colors duration-200 group min-h-[44px]',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-2 focus-visible:ring-offset-chamber-rail',
          collapsed ? 'px-3 py-3 justify-center' : 'px-3 py-3 gap-3',
          isActive
            ? 'bg-brass text-chamber-ink'
            : 'text-chamber-muted hover:bg-white/[0.03] hover:text-parchment'
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cn(
              'h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-105',
              isActive ? 'text-chamber-ink' : 'text-chamber-muted-dim group-hover:text-parchment'
            )}
          />
          {!collapsed && (
            <span className="text-[13.5px] font-medium truncate flex-1">{item.label}</span>
          )}
          {!collapsed && isActive && (
            <ChevronRight className="h-4 w-4 text-chamber-ink/70 shrink-0" />
          )}
          {!collapsed && !isActive && item.shortcut && (
            <span className="text-[10px] font-plex text-chamber-muted-faint opacity-0 group-hover:opacity-100 transition-opacity">
              {item.shortcut}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return <Tooltip content={item.label} side="right">{link}</Tooltip>;
  }
  return link;
};

/**
 * Cross-app handoff to the Corporate Suite. The corporate app has no login of
 * its own, so we mint a Firebase custom token here and full-navigate to its
 * `/sso` route (see `launchCorporateSuite`). Rendered only for users whose
 * primary_practice_area === 'corporate'.
 */
// Rail collapse control. ONE definition rendered in two places: inline in the
// logo header when the rail is expanded (where there is room beside the
// lockup), and on its own centred row when collapsed (the 72px rail's header
// is justify-center and cannot hold the lockup and this button side by side).
const CollapseToggle: React.FC<{ collapsed: boolean; toggle: () => void }> = ({ collapsed, toggle }) => (
  <button
    onClick={toggle}
    className="hidden lg:flex p-1.5 text-chamber-muted-faint hover:bg-white/[0.03] hover:text-parchment transition-colors"
    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
  >
    {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
  </button>
);

const CorporateSuiteLink: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const [busy, setBusy] = useState(false);

  const handleLaunch = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await launchCorporateSuite();
      // On success the browser navigates away; no need to reset `busy`.
    } catch (err) {
      console.error('Failed to open Corporate Suite:', err);
      setBusy(false);
    }
  };

  const link = (
    <button
      type="button"
      onClick={() => void handleLaunch()}
      disabled={busy}
      className={cn(
        'relative flex w-full items-center transition-colors duration-200 group min-h-[44px] mb-1',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-2 focus-visible:ring-offset-chamber-rail',
        'border border-brass text-brass hover:bg-brass hover:text-chamber-ink disabled:opacity-70',
        collapsed ? 'px-3 py-3 justify-center' : 'px-3 py-3 gap-3'
      )}
    >
      <ExternalLink className="h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-105" />
      {!collapsed && (
        <span className="text-[13.5px] font-semibold truncate flex-1 text-left">
          {busy ? 'Opening…' : 'Corporate Suite'}
        </span>
      )}
    </button>
  );

  if (collapsed) {
    return <Tooltip content="Corporate Suite" side="right">{link}</Tooltip>;
  }
  return link;
};

const Sidebar: React.FC = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [supportDeskOn, setSupportDeskOn] = useState(false);
  const { collapsed, mobileOpen, toggle, setMobileOpen } = useSidebar();
  const location = useLocation();
  const { activePracticeArea } = usePreferences();
  const reduxUser = useSelector((state: RootState) => state.auth.user);
  const navFlags = useNavFlags();
  // Lawyer-only product — nav is always the lawyer persona.
  const config = getPersonaConfig('lawyer');
  // Global nav lens: active switcher → user's primary practice area → all areas.
  const lens = effectiveLens(
    activePracticeArea,
    (reduxUser?.primary_practice_area as PracticeArea | null) ?? null,
  );
  // The workspace boundary for THIS tab. Re-read on navigation so a switch
  // (which routes before it reloads) cannot leave the rail on the old tools.
  const activeRole = useMemo(readActiveRole, [location.pathname, activePracticeArea]);
  // User-chip sub-line: the ACTIVE WORKSPACE's name, else email. Must resolve
  // from `activeRole` (the per-tab boundary every API call is scoped by), NOT
  // from `primary_practice_area` — a stored value a workspace switch never
  // touches, so the chip kept saying "Criminal Practice" in a civil workspace
  // while the switcher directly above it said "Consumer & Civil". Same
  // derivation and same single label source as LawyerDashboard (the C1 fix)
  // and PracticeAreaSwitcher — see `workspaceLabel`, which owns the rule.
  const practiceLabel = workspaceLabel(activeRole, lens);

  useEffect(() => {
    if (!user) { setIsAdmin(false); setSupportDeskOn(false); return; }
    adminApi.checkAdminAccess().then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
    // Support Desk link: the backend answers 200 only for staff/admin while the desk is on.
    supportApi.status().then(() => setSupportDeskOn(true)).catch(() => setSupportDeskOn(false));
  }, [user]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname, setMobileOpen]);

  const sidebarContent = (
    <div className="flex flex-col h-full bg-chamber-rail text-chamber-muted font-instrument">
      {/* Logo header (replaces app-level header space on this rail) */}
      <div className={cn('flex items-center h-14 border-b border-brass-hairline-faint shrink-0', collapsed ? 'justify-center px-2' : 'justify-between px-4')}>
        <Link
          to="/"
          className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-2 focus-visible:ring-offset-chamber-rail"
          aria-label="iAdvocate home"
        >
          <LogoLockup collapsed={collapsed} />
        </Link>

        <div className="flex items-center gap-1">
          {/* Desktop collapse — inline here rather than on a row of its own,
              which cost 36px of empty rail under the workspace switcher. */}
          {!collapsed && <CollapseToggle collapsed={collapsed} toggle={toggle} />}

          {/* Mobile close */}
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1.5 text-chamber-muted-dim hover:bg-white/[0.03] hover:text-parchment"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Practice-area workspace lens */}
      <div className="pt-3">
        <PracticeAreaSwitcher collapsed={collapsed} />
      </div>

      {/* Expand control, collapsed rail only — expanded, it lives in the header. */}
      {collapsed && (
        <div className="hidden lg:flex justify-center px-2 pt-2">
          <CollapseToggle collapsed={collapsed} toggle={toggle} />
        </div>
      )}

      {/* Main nav — grouped by section */}
      <nav className="sidebar-scroll flex-1 overflow-y-auto px-2 mt-4 pb-4">
        {(() => {
          const items = config.navItems.filter(i => navItemVisible(i, lens, activeRole, navFlags));
          const unsectioned = items.filter(i => !i.section);
          const sections: string[] = [];
          items.forEach(i => { if (i.section && !sections.includes(i.section)) sections.push(i.section); });

          return (
            <>
              {/* Unsectioned (Dashboard etc.) */}
              {unsectioned.length > 0 && (
                <ul className="space-y-0.5">
                  {unsectioned.map((item) => (
                    <li key={item.path}>
                      <NavItemLink item={item} collapsed={collapsed} />
                    </li>
                  ))}
                </ul>
              )}

              {/* Grouped sections */}
              {sections.map((section, sectionIdx) => (
                <div
                  key={section}
                  className={cn((sectionIdx > 0 || unsectioned.length > 0) && 'mt-5')}
                >
                  {/* Brass hairline separates each group for a quick eye. Skipped
                      above the first group when there's no unsectioned block, so
                      the rail doesn't open with a stray rule under the switcher. */}
                  {!collapsed && (sectionIdx > 0 || unsectioned.length > 0) && (
                    <div className="h-px bg-brass-hairline-soft mx-3 mb-3" />
                  )}
                  {!collapsed && (
                    <p className="px-3 mb-2 font-plex text-[10px] font-bold uppercase tracking-[0.3em] text-brass">
                      {section}
                    </p>
                  )}
                  {collapsed && <div className="h-px bg-brass-hairline-faint mx-2 mb-2" />}
                  <ul className="space-y-0.5">
                    {items.filter(i => i.section === section).map((item) => (
                      <li key={item.path}>
                        <NavItemLink item={item} collapsed={collapsed} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Legacy toolItems (for layperson/judge) */}
              {config.toolItems.length > 0 && (
                <div className="mt-5">
                  {!collapsed && (
                    <p className="px-3 mb-2 font-plex text-[10px] uppercase tracking-[0.3em] text-chamber-muted-faint">Tools</p>
                  )}
                  <ul className="space-y-0.5">
                    {config.toolItems.map((item) => (
                      <li key={item.path}>
                        <NavItemLink item={item} collapsed={collapsed} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Admin */}
              {isAdmin && (
                <div className="mt-5">
                  {!collapsed && (
                    <p className="px-3 mb-2 font-plex text-[10px] uppercase tracking-[0.3em] text-chamber-muted-faint">Admin</p>
                  )}
                  {staffNavItems({ isAdmin, supportDeskOn }).map((item) => (
                    <NavItemLink
                      key={item.path}
                      item={{ ...item, icon: item.path === '/admin/support' ? Headset : BarChart3 }}
                      collapsed={collapsed}
                    />
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </nav>

      {/* Bottom: Corporate Suite (corporate lawyers only) + Settings + User */}
      <div className="shrink-0 border-t border-brass-hairline-faint px-2 py-3">
        {reduxUser?.primary_practice_area === 'corporate' && (
          <CorporateSuiteLink collapsed={collapsed} />
        )}
        <NavItemLink item={{ path: '/settings', label: 'Settings', icon: Settings }} collapsed={collapsed} />
        {!collapsed && user && (
          <div className="mt-3 mx-1 flex items-center gap-3 px-2 py-2 bg-white/[0.03]">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0 bg-brass text-chamber-ink font-display">
              {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <p className="text-[13.5px] font-medium text-parchment truncate">{user.displayName || 'User'}</p>
                {/* Plan tier, always visible. A lawyer who is paying should be
                    able to see WHAT they are paying for without hunting for
                    it, and one on a trial should be reminded before it ends.
                    Kept to the tier word only — the full plan, slot count and
                    expiry live on /profile. */}
                <PlanBadge />
              </div>
              <p className="font-plex text-[10px] uppercase tracking-wide text-chamber-muted-dim truncate">
                {practiceLabel ?? user.email}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-40 transition-all duration-300 shadow-xl',
          collapsed ? 'w-[72px]' : 'w-64'
        )}
      >
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-chamber-rail/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="fixed left-0 top-0 bottom-0 w-[280px] z-50 lg:hidden shadow-2xl"
            >
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
